import React, { useState } from 'react';
import {
  Accordion,
  Button,
  Flex,
  HelpText,
  Tag,
  Text,
  Tooltip,
  useId,
} from '@applyboard/crystal-ui';
import { InfoOutlineIcon } from '@applyboard/ui-icons';
import { ApplicationActionName } from '~/types';
import { ChangeIntakeModal } from '../ChangeIntakeModal';
import { useApplicationPagePermissions } from '../../data/permissions';
import { RecommendedProgramsDialog } from '../RecommendedProgramsDialog';
import { ColorIndicator } from '~/components/ColorIndicator';
import {
  useApplication,
  useApplicationAvailableActions,
  useApplicationConfiguration,
  useCurrentInsightScoreToDisplay,
} from '../../data';
import { useEventTracker, useTrackViewEvent } from '@applyboard/ui-utils';

type InsightsCardProps = {
  school?: {
    id?: number | string;
    country?: string;
  };
  program?: {
    id?: number | string;
    level?: string;
  };
  academicIntake?: string;
  eslIntake?: string;
  reloadBaseApplication?: () => Promise<void>;
  onChangeIntakeSuccess?: (data: {
    academicIntake?: string;
    academicIntakeId?: number;
    eslIntake?: string;
    eslIntakeId?: number;
  }) => void;
};

export const InsightsCard = (props: InsightsCardProps) => {
  const insightId = useId();
  const { permissions } = useApplicationPagePermissions();
  const [showRecommendedProgramsDialog, setShowRecommendedProgramsDialog] = useState(false);
  const { application } = useApplication();
  const { canPerformApplicationAction } = useApplicationAvailableActions();
  const { applicationConfiguration } = useApplicationConfiguration();
  const displayedScore = useCurrentInsightScoreToDisplay();
  const scoreConfig = applicationConfiguration?.applicationSuccessScoreLevels.find(
    score => score.level === displayedScore?.seatAvailability?.determination,
  );

  const viewSuccessScoreCtaThreshold =
    applicationConfiguration?.viewSuccessScoreCtaThreshold ?? null;

  const isCtaVisible =
    viewSuccessScoreCtaThreshold &&
    displayedScore?.seatAvailability &&
    displayedScore?.seatAvailability?.score < viewSuccessScoreCtaThreshold;

  const eventTrackingData = {
    programId: props.program?.id,
    programLevel: props.program?.level,
    schoolId: props.school?.id,
    schoolCountry: props.school?.country,
    currentAcademicIntake: props.academicIntake,
    currentESLIntake: props.eslIntake,
    showingChangeIntakeCTA:
      isCtaVisible && canPerformApplicationAction(ApplicationActionName.CHANGE_INTAKE),
    showingRecommendedProgramsCTA:
      isCtaVisible && canPerformApplicationAction(ApplicationActionName.RECOMMENDED_PROGRAM),
  };

  const { trackEvent } = useEventTracker({ data: eventTrackingData });

  const isCardVisible =
    permissions?.view_insights &&
    displayedScore &&
    scoreConfig &&
    (application.stageId === 'pre-payment' ||
      application.stageId === 'pre-submission' ||
      application.stageId === 'submission');

  // We temporarily handle the special case where the success score is 0 in FE,
  // using the following hardcoded score label and help text. Ideally, during the revamp
  // of this page, we can make sure we got all the data from BE.
  const insightCardScoreText =
    displayedScore?.seatAvailability?.score && displayedScore.seatAvailability.score > 0 ? (
      <Text>{`${scoreConfig?.level} (${displayedScore.seatAvailability.score}%)`}</Text>
    ) : (
      <Text>0% (Intake closed)</Text>
    );
  const helpText =
    displayedScore?.seatAvailability?.score && displayedScore.seatAvailability.score > 0
      ? scoreConfig?.helpText
      : 'The intake for this program is closed. Please change the intake or consider a recommended program.';

  return (
    <div css={{ display: isCardVisible ? 'block' : 'none' }}>
      {props.program && props.school && props.academicIntake && isCardVisible && (
        <InsightsCardEventTracker eventTrackingData={eventTrackingData} />
      )}
      <Flex py={6} px={4}>
        <Flex.Item grow={1}>
          <Accordion
            onChange={nonCollapsedAccordionItems => {
              if (
                Array.isArray(nonCollapsedAccordionItems) &&
                !nonCollapsedAccordionItems.find(item => item === insightId)
              ) {
                trackEvent({
                  event: 'Single Application - Insights Card - Clicked Chevron To Collapse',
                });
              }
            }}
          >
            <Accordion.Item
              autoOpen
              id={insightId}
              title={
                <Flex align="center" gap={2}>
                  <Text variant="titleS">Insights</Text>
                  <Tag intent="secondary">Beta</Tag>
                </Flex>
              }
            >
              <Flex gap={4}>
                <Flex.Item basis="50%">
                  <Text variant="labelM">
                    <Flex wrap align="center">
                      Success&nbsp;
                      <Flex align="center">
                        score&nbsp;
                        <Tooltip label="The score is a prediction of the application's potential for admission and is based on various factors, including ApplyBoard's historical data">
                          <Button
                            aria-label="More information about success score"
                            variant="transparent"
                            size="sm"
                            leadIcon={InfoOutlineIcon}
                            onPointerMove={() => {
                              trackEvent({
                                event:
                                  'Single Application - Insights Card - Success Score - Hovered On Tooltip',
                              });
                            }}
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </Text>
                </Flex.Item>
                <Flex.Item basis="50%">
                  <Flex align="center" pt={2}>
                    <ColorIndicator colorCode={scoreConfig?.colorCode} marginRight="8px" />
                    {insightCardScoreText}
                  </Flex>
                </Flex.Item>
              </Flex>
              <Flex pt={2}>
                <HelpText
                  showIcon={scoreConfig?.helpTextIntent === 'warning'}
                  intent={scoreConfig?.helpTextIntent}
                >
                  {helpText}
                </HelpText>
              </Flex>
              <div css={{ display: isCtaVisible ? 'block' : 'none' }}>
                <Flex pt={4} gap={2} direction={{ xs: 'column', xl: 'row' }}>
                  <ChangeIntakeModal
                    origin="insights card"
                    onClick={() => {
                      trackEvent({
                        event:
                          'Single Application - Insights Card - Success Score - Clicked On Change Intake Button',
                      });
                    }}
                    onSuccess={async data => {
                      await props.reloadBaseApplication?.();
                      props.onChangeIntakeSuccess?.(data);
                    }}
                  />
                  <RecommendedProgramsDialog
                    isOpen={showRecommendedProgramsDialog}
                    onOpenChange={setShowRecommendedProgramsDialog}
                    origin="insights card"
                    school={props.school}
                    program={props.program}
                    academicIntake={props.academicIntake}
                    eslIntake={props.eslIntake}
                    onClick={() => {
                      trackEvent({
                        event:
                          'Single Application - Insights Card - Success Score - Clicked On See Recommended Programs Button',
                      });
                    }}
                  />
                </Flex>
              </div>
            </Accordion.Item>
          </Accordion>
        </Flex.Item>
      </Flex>
    </div>
  );
};

const InsightsCardEventTracker = ({
  eventTrackingData,
}: {
  eventTrackingData: {
    programId: string | number | undefined;
    programLevel: string | undefined;
    schoolId: string | number | undefined;
    schoolCountry: string | undefined;
    currentAcademicIntake: string | undefined;
    currentESLIntake: string | undefined;
  };
}) => {
  useTrackViewEvent({ event: 'Single Application - Insights Card', data: eventTrackingData });
  return <></>;
};
